import dayjs, {Dayjs} from "dayjs";
import {buildEventCreateDto} from "./eventCreatePayload";
import type {EventCreateFormData} from "./eventCreatePayload";
import {
    DEFAULT_EVENT_PUBLIC_ASSET_MODE,
    EventPublicAssetModeValue,
    normalizeEventPublicAssetMode
} from "../../eventPublicAssetMode";
import {atiColors} from "../../Theme/atiTheme";

export type EventCreationWizardValues = EventCreateFormData & {
    logoUrl: string;
    primaryColor: string;
    secondaryColor: string;
    publicAssetMode: EventPublicAssetModeValue;
    programPdfUrl: string;
    venueLayoutImageUrl: string;
    sponsorsImageUrl: string;
    badgeTemplateUrl: string;
    badgeBackTemplateUrl: string;
    badgeFirstNameX?: number | null;
    badgeFirstNameY?: number | null;
    badgeLastNameX?: number | null;
    badgeLastNameY?: number | null;
    badgeCompanyX?: number | null;
    badgeCompanyY?: number | null;
    badgeQrCodeX?: number | null;
    badgeQrCodeY?: number | null;
    badgeTextFieldsJson: string;
    headerImageUrl: string;
    certificateTemplateUrl: string;
    certificateTextFieldsJson: string;
    startDate?: Dayjs | Date | string | number;
    endDate?: Dayjs | Date | string | number;
};

export const DEFAULT_EVENT_PRIMARY_COLOR = atiColors.blue;
export const DEFAULT_EVENT_SECONDARY_COLOR = atiColors.orange;

export const eventCreationStartFieldNames: Array<keyof EventCreationWizardValues> = [
    "name",
    "place",
    "address",
    "startDate",
    "endDate",
    "hasParticipationFee"
];

const emptyText = "";
const buildTemporaryLegacyEventCode = () => `EVENT-${dayjs().format("YYYYMMDDHHmmssSSS")}`;

export const createDefaultEventCreationWizardValues = (): EventCreationWizardValues => ({
    code: buildTemporaryLegacyEventCode(),
    name: emptyText,
    nameHr: emptyText,
    nameEn: emptyText,
    place: emptyText,
    address: emptyText,
    eventInformationHr: emptyText,
    eventInformationEn: emptyText,
    onSiteRegistrationInstructionsHr: emptyText,
    onSiteRegistrationInstructionsEn: emptyText,
    hasParticipationFee: false,
    startDate: dayjs().startOf("day"),
    endDate: dayjs().add(1, "day").startOf("day"),
    logoUrl: emptyText,
    primaryColor: DEFAULT_EVENT_PRIMARY_COLOR,
    secondaryColor: DEFAULT_EVENT_SECONDARY_COLOR,
    publicAssetMode: DEFAULT_EVENT_PUBLIC_ASSET_MODE,
    programPdfUrl: emptyText,
    venueLayoutImageUrl: emptyText,
    sponsorsImageUrl: emptyText,
    badgeTemplateUrl: emptyText,
    badgeBackTemplateUrl: emptyText,
    badgeFirstNameX: null,
    badgeFirstNameY: null,
    badgeLastNameX: null,
    badgeLastNameY: null,
    badgeCompanyX: null,
    badgeCompanyY: null,
    badgeQrCodeX: null,
    badgeQrCodeY: null,
    badgeTextFieldsJson: emptyText,
    headerImageUrl: emptyText,
    certificateTemplateUrl: emptyText,
    certificateTextFieldsJson: emptyText
});

const textOrFallback = (value: string | undefined | null, fallback: string) => value?.trim() || fallback;
const textOrEmpty = (value: string | undefined | null) => value?.trim() ?? emptyText;
const validColorOrFallback = (value: string | undefined | null, fallback: string) =>
    /^#[0-9A-Fa-f]{6}$/.test(value?.trim() ?? "") ? value!.trim() : fallback;

export const buildEventCreationWizardSubmitValues = (
    values: EventCreationWizardValues
): EventCreationWizardValues => {
    const defaults = createDefaultEventCreationWizardValues();
    const normalized = {
        ...defaults,
        ...values
    };
    const name = textOrFallback(normalized.name, "New event");
    const eventInformationHr = textOrEmpty(normalized.eventInformationHr);
    const eventInformationEn = textOrEmpty(normalized.eventInformationEn);
    const onSiteRegistrationInstructionsHr = textOrEmpty(normalized.onSiteRegistrationInstructionsHr);
    const onSiteRegistrationInstructionsEn = textOrEmpty(normalized.onSiteRegistrationInstructionsEn);

    return {
        ...normalized,
        code: textOrFallback(normalized.code, buildTemporaryLegacyEventCode()),
        nameHr: textOrFallback(normalized.nameHr, name),
        nameEn: textOrFallback(normalized.nameEn, name),
        eventInformationHr,
        eventInformationEn,
        onSiteRegistrationInstructionsHr,
        onSiteRegistrationInstructionsEn,
        primaryColor: validColorOrFallback(normalized.primaryColor, DEFAULT_EVENT_PRIMARY_COLOR),
        secondaryColor: validColorOrFallback(normalized.secondaryColor, DEFAULT_EVENT_SECONDARY_COLOR),
        publicAssetMode: normalizeEventPublicAssetMode(normalized.publicAssetMode),
        hasParticipationFee: normalized.hasParticipationFee ?? false
    };
};

export const buildEventBasicSetupSaveInputFromCreateValues = (
    eventId: number,
    values: EventCreationWizardValues
) => {
    const normalized = buildEventCreationWizardSubmitValues(values);

    return {
        ...buildEventCreateDto(normalized),
        id: eventId,
        logoUrl: normalized.logoUrl ?? emptyText,
        primaryColor: normalized.primaryColor,
        secondaryColor: normalized.secondaryColor,
        publicAssetMode: normalized.publicAssetMode,
        programPdfUrl: normalized.programPdfUrl ?? emptyText,
        venueLayoutImageUrl: normalized.venueLayoutImageUrl ?? emptyText,
        sponsorsImageUrl: normalized.sponsorsImageUrl ?? emptyText,
        badgeTemplateUrl: normalized.badgeTemplateUrl ?? emptyText,
        badgeBackTemplateUrl: normalized.badgeBackTemplateUrl ?? emptyText,
        badgeFirstNameX: normalized.badgeFirstNameX ?? null,
        badgeFirstNameY: normalized.badgeFirstNameY ?? null,
        badgeLastNameX: normalized.badgeLastNameX ?? null,
        badgeLastNameY: normalized.badgeLastNameY ?? null,
        badgeCompanyX: normalized.badgeCompanyX ?? null,
        badgeCompanyY: normalized.badgeCompanyY ?? null,
        badgeQrCodeX: normalized.badgeQrCodeX ?? null,
        badgeQrCodeY: normalized.badgeQrCodeY ?? null,
        badgeTextFieldsJson: normalized.badgeTextFieldsJson ?? emptyText,
        headerImageUrl: normalized.headerImageUrl ?? emptyText,
        certificateTemplateUrl: normalized.certificateTemplateUrl ?? emptyText,
        certificateParticipantNameX: null,
        certificateParticipantNameY: null,
        certificateTextFieldsJson: normalized.certificateTextFieldsJson ?? emptyText
    };
};

const parsePositiveIntegerId = (value: unknown): number | undefined => {
    if (typeof value === "number") {
        return Number.isInteger(value) && value > 0 ? value : undefined;
    }

    if (typeof value === "string" && /^\d+$/.test(value.trim())) {
        const parsed = Number(value);

        return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : undefined;
    }

    return undefined;
};

const extractEventIdFromPayload = (payload: any): number | undefined => {
    const directId = parsePositiveIntegerId(payload?.id);

    if (directId) {
        return directId;
    }

    if (Array.isArray(payload)) {
        for (const item of payload) {
            const itemId = extractEventIdFromPayload(item);

            if (itemId) {
                return itemId;
            }
        }

        return undefined;
    }

    if (!payload || typeof payload !== "object") {
        return undefined;
    }

    return (
        extractEventIdFromPayload(payload.item) ??
        extractEventIdFromPayload(payload.node) ??
        extractEventIdFromPayload(payload.event) ??
        extractEventIdFromPayload(payload.createdEvent) ??
        extractEventIdFromPayload(payload.items) ??
        extractEventIdFromPayload(payload.nodes) ??
        extractEventIdFromPayload(payload.edges)
    );
};

export const extractCreatedEventId = (response: any): number | undefined => {
    return extractEventIdFromPayload(response?.data?.createEvent);
};
